import { useState, type FormEvent } from 'react';

import { submitLead, type LeadResult } from '../lib/offerings';
import { ApiError } from '../lib/apiClient';
import './ApplyToInvest.css';

/**
 * The accreditation screener and lead capture behind "Apply to Invest".
 *
 * THE CTA IS "APPLY", NOT "INVEST". Nothing in this component moves money,
 * creates an account, or executes anything. The best outcome is that a person
 * at Voleto gets in touch. That is not a UX preference: a 506(c) offering is
 * sold to verified accredited investors after a relationship and a
 * verification step, so a self-serve "Invest Now" button would misrepresent
 * what actually happens next.
 *
 * The screener comes FIRST and gates the form that follows:
 *
 *   Not accredited -> a thank-you and an OPTIONAL mailing-list signup. No
 *                     desired amount is asked for, and if one were somehow
 *                     supplied the engine discards it.
 *   Accredited     -> name, email, desired amount, sent as an inquiry.
 *
 * The portal does not enforce that split by itself — the engine decides from
 * the attestation and a database CHECK backs it up. This component's job is to
 * make the question unavoidable and to show the definition the visitor is
 * attesting against, rather than letting them guess what "accredited" means.
 */

/**
 * Rule 501(a) of Regulation D, in the categories that apply to the people who
 * reach this page. Shown in full rather than linked: someone is about to make a
 * representation about their own status, and hiding the standard behind a link
 * makes that representation less meaningful.
 */
const ACCREDITED_CRITERIA = [
  {
    key: 'income',
    label: 'Income',
    text: 'Individual income over $200,000, or $300,000 jointly with a spouse or spousal '
      + 'equivalent, in each of the two most recent years, with a reasonable expectation '
      + 'of the same this year.',
  },
  {
    key: 'net_worth',
    label: 'Net worth',
    text: 'Individual or joint net worth over $1,000,000, excluding the value of your '
      + 'primary residence.',
  },
  {
    key: 'professional',
    label: 'Licence',
    text: 'A Series 7, Series 65, or Series 82 licence held in good standing.',
  },
  {
    key: 'entity',
    label: 'Entity',
    text: 'An entity with more than $5,000,000 in assets, or one in which every equity '
      + 'owner is an accredited investor.',
  },
];

type Stage = 'screener' | 'accredited' | 'not-accredited' | 'done';

interface Props {
  /** Omitted on the general "register interest" route, which names no deal. */
  offeringId?: string;
  offeringTitle?: string;
}

export function ApplyToInvest({ offeringId, offeringTitle }: Props) {
  const [stage, setStage] = useState<Stage>('screener');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LeadResult | null>(null);

  async function send(accredited: boolean) {
    setSubmitting(true);
    setError(null);
    try {
      // Dollars in the form, cents on the wire — the engine speaks cents only.
      const dollars = Number(amount);
      const desiredAmountCents =
        accredited && amount.trim() !== '' && Number.isFinite(dollars) && dollars > 0
          ? Math.round(dollars * 100)
          : null;

      const lead = await submitLead({
        offeringId: offeringId ?? null,
        name: name.trim() === '' ? null : name.trim(),
        email,
        desiredAmountCents,
        accredited,
      });
      setResult(lead);
      setStage('done');
    } catch (err: unknown) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(event: FormEvent, accredited: boolean) {
    event.preventDefault();
    void send(accredited);
  }

  // ── Done ──────────────────────────────────────────────────────────────────
  if (stage === 'done' && result) {
    const isInquiry = result.kind === 'investor_inquiry';
    return (
      <div className="apply apply--done">
        <h3 className="apply__title">
          {isInquiry ? 'Application received' : 'Thanks, you’re on the list'}
        </h3>
        <p className="apply__body">
          {isInquiry ? (
            <>
              Someone from Voleto will be in touch about
              {result.offeringTitle ? ` ${result.offeringTitle}` : ' this offering'}.
              Nothing has been bought or committed, and no payment has been taken.
            </>
          ) : (
            <>
              We’ll let you know when there’s something you can take part in. You have not
              applied to invest, and nothing has been committed.
            </>
          )}
        </p>
      </div>
    );
  }

  // ── Screener ──────────────────────────────────────────────────────────────
  if (stage === 'screener') {
    return (
      <div className="apply">
        <h3 className="apply__title">
          {offeringTitle ? 'Apply to invest' : 'Register interest'}
        </h3>
        <p className="apply__question">Are you an accredited investor?</p>

        <p className="apply__body">
          This offering is made under Rule 506(c) of Regulation D and is available only to
          accredited investors. You meet the definition if any one of the following is
          true:
        </p>

        <ul className="apply__criteria">
          {ACCREDITED_CRITERIA.map((criterion) => (
            <li key={criterion.key} className="apply__criterion">
              <span className="apply__criterion-label">{criterion.label}</span>
              <span className="apply__criterion-text">{criterion.text}</span>
            </li>
          ))}
        </ul>

        <p className="apply__note">
          Your answer is a self-declaration. If you apply, Voleto will separately verify
          your status before any investment can be made, saying yes here does not
          complete that step, and does not commit you to anything.
        </p>

        <div className="apply__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={() => setStage('accredited')}
          >
            Yes, I’m accredited
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setStage('not-accredited')}
          >
            No / not sure
          </button>
        </div>
      </div>
    );
  }

  // ── Not accredited: thank you + optional mailing list ─────────────────────
  if (stage === 'not-accredited') {
    return (
      <div className="apply">
        <h3 className="apply__title">Thanks for your interest</h3>
        <p className="apply__body">
          This offering is only open to accredited investors, so we can’t take an
          application from you today. That is a legal restriction on the offering, not a
          judgement about you.
        </p>
        <p className="apply__body">
          If you’d like, leave your email and we’ll let you know when Voleto has something
          open to everyone. This is optional, you can simply close this.
        </p>

        <form className="apply__form" onSubmit={(event) => onSubmit(event, false)}>
          <div className="field">
            <label className="field__label" htmlFor="ml-email">
              Email
            </label>
            <input
              id="ml-email"
              className="field__input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </div>

          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}

          <div className="apply__actions">
            <button type="submit" className="button button--primary" disabled={submitting}>
              {submitting ? 'Adding…' : 'Keep me posted'}
            </button>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setStage('screener')}
              disabled={submitting}
            >
              Back
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── Accredited: the lead form ─────────────────────────────────────────────
  return (
    <div className="apply">
      <h3 className="apply__title">Apply to invest</h3>
      <p className="apply__body">
        Tell us how to reach you and roughly what size of participation you have in mind.
        This is an expression of interest, it takes no payment, creates no account, and
        commits you to nothing.
      </p>

      <form className="apply__form" onSubmit={(event) => onSubmit(event, true)}>
        <div className="field">
          <label className="field__label" htmlFor="lead-name">
            Name
          </label>
          <input
            id="lead-name"
            className="field__input"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Your name"
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="lead-email">
            Email
          </label>
          <input
            id="lead-email"
            className="field__input"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="lead-amount">
            Amount you’re considering (USD, optional)
          </label>
          <input
            id="lead-amount"
            className="field__input mono"
            type="number"
            min="0"
            step="1000"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="50000"
          />
        </div>

        <p className="apply__note">
          By applying you confirm the accreditation statement you just made. Voleto will
          verify it before any investment is possible.
        </p>

        {error && (
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        )}

        <div className="apply__actions">
          <button type="submit" className="button button--primary" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send application'}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setStage('screener')}
            disabled={submitting}
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
}
